import * as React from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CommunityMessageAttachments } from '@/modules/comunidade/components/CommunityMessageAttachments'
import type { CommunityMessageAttachment } from '@/modules/comunidade/services/communityService'
import { isImageAttachment } from '@/modules/comunidade/utils/communityAttachments'
import type { PostAttachment } from '../types'

export type LightboxVariant = 'fullscreen' | 'drawer'

type PostAttachmentsProps = {
  attachments: PostAttachment[]
  lightboxVariant?: LightboxVariant
}

function normalizeAttachments(attachments: PostAttachment[]): CommunityMessageAttachment[] {
  const list = Array.isArray(attachments) ? attachments.filter((a) => a && a.url) : []
  return list.map((att) => ({
    id: att.id,
    messageId: att.postId,
    url: att.url,
    storageBucket: att.storageBucket,
    storagePath: att.storagePath,
    contentType: att.contentType,
    sizeBytes: att.sizeBytes,
    createdAt: att.createdAt,
  }))
}

export function PostAttachments({ attachments, lightboxVariant = 'fullscreen' }: PostAttachmentsProps) {
  const normalized = React.useMemo(() => normalizeAttachments(attachments), [attachments])

  const [lightboxOpen, setLightboxOpen] = React.useState(false)
  const [lightboxAttachments, setLightboxAttachments] = React.useState<
    CommunityMessageAttachment[]
  >([])
  const [lightboxIndex, setLightboxIndex] = React.useState(0)

  const handleOpenLightbox = React.useCallback(
    (items: CommunityMessageAttachment[], index: number) => {
      const normalizedImages = Array.isArray(items)
        ? items.filter((a) => a && a.url && isImageAttachment(a))
        : []
      if (normalizedImages.length === 0) return

      const safeIndex = Number.isFinite(index)
        ? Math.max(0, Math.min(index, normalizedImages.length - 1))
        : 0

      setLightboxAttachments(normalizedImages)
      setLightboxIndex(safeIndex)
      setLightboxOpen(true)
    },
    [],
  )

  React.useEffect(() => {
    if (lightboxOpen) return
    setLightboxAttachments([])
    setLightboxIndex(0)
  }, [lightboxOpen])

  const hasLightbox = lightboxOpen && lightboxAttachments.length > 0
  const total = lightboxAttachments.length
  const currentIndex =
    hasLightbox && total > 0 ? Math.max(0, Math.min(lightboxIndex, total - 1)) : 0
  const current = hasLightbox && total > 0 ? lightboxAttachments[currentIndex] : null

  const isDrawer = lightboxVariant === 'drawer'

  const overlayClassName = isDrawer
    ? 'fixed inset-y-0 left-0 right-0 lg:right-[30vw] z-[200000] flex items-center justify-center bg-black/80 px-6 py-6'
    : 'fixed inset-0 z-[200000] flex items-center justify-center bg-black/80 px-4 py-6'

  return (
    <>
      <CommunityMessageAttachments
        attachments={normalized}
        onOpenLightbox={handleOpenLightbox}
      />

      {hasLightbox && current && (
        <div className={overlayClassName}>
          <div
            className={cn(
              'relative flex w-full items-center justify-center pt-10',
              isDrawer ? 'pb-16 max-w-full' : 'pb-12 max-w-[80vw]',
            )}
          >
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="absolute right-0 top-2 inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white hover:bg-black/80"
            >
              <X className="h-4 w-4" />
              Fechar
            </button>

            {total > 1 && (
              <button
                type="button"
                onClick={() => {
                  if (!hasLightbox || total <= 1) return
                  setLightboxIndex((prev) => (prev <= 0 ? total - 1 : prev - 1))
                }}
                className="absolute left-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label="Imagem anterior"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            <div className="flex w-full items-center justify-center">
              <img
                src={current.url}
                alt="Imagem anexada ao post"
                className={cn(
                  'h-auto w-auto max-w-full rounded-lg object-contain shadow-2xl',
                  isDrawer ? 'max-h-[calc(100dvh-18rem)]' : 'max-h-[calc(100dvh-14rem)]',
                )}
              />
            </div>

            {total > 1 && (
              <button
                type="button"
                onClick={() => {
                  if (!hasLightbox || total <= 1) return
                  setLightboxIndex((prev) => (prev >= total - 1 ? 0 : prev + 1))
                }}
                className="absolute right-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label="Proxima imagem"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}

            {total > 1 && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                {currentIndex + 1} / {total}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
